import { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, Send, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../../services/api';
import { useWorkspace } from '../WorkspaceContext';
import { SERA } from '../mocks/sera';

const DEMO_PROMPTS = [
  "What's the next appointment?",
  'How much revenue have we made today?',
  "Which clients haven't visited in 60 days?",
];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const TypingIndicator = () => (
  <div className="flex justify-start" aria-live="polite" aria-label={`${SERA.name} is typing`}>
    <div className="flex items-center gap-1.5 bg-sand-100 px-4 py-3 ring-1 ring-ink-900/10">
      <span className="h-2 w-2 animate-bounce rounded-full bg-ink-900/40 [animation-delay:-0.3s]" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-ink-900/40 [animation-delay:-0.15s]" />
      <span className="h-2 w-2 animate-bounce rounded-full bg-ink-900/40" />
    </div>
  </div>
);

/**
 * Floating Business Agent — bottom-left FAB opens a right-half chat panel.
 * Stays available on every workspace screen for the demo.
 */
const BusinessAgentDock = () => {
  const { business, activeLocation } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(true);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const [suggestions, setSuggestions] = useState(DEMO_PROMPTS);
  const [messages, setMessages] = useState([]);
  const bottomRef = useRef(null);
  const streamCancelRef = useRef(false);
  const inputRef = useRef(null);

  // Deep link: /app/...?agent=1
  useEffect(() => {
    if (searchParams.get('agent') === '1') {
      setOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!open) return undefined;
    setMessages((prev) =>
      prev.length
        ? prev
        : [
            {
              id: 'welcome',
              role: 'agent',
              text: `Hi — I'm ${SERA.name}. Ask me about your next appointment, revenue, or clients at ${
                business?.name || 'your salon'
              }.`,
            },
          ]
    );
    const t = setTimeout(() => inputRef.current?.focus(), 200);
    const onKey = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, business?.name]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing, suggestions, open]);

  useEffect(() => {
    return () => {
      streamCancelRef.current = true;
    };
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    if (searchParams.get('agent')) {
      const next = new URLSearchParams(searchParams);
      next.delete('agent');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openPanel = useCallback(() => {
    setTipOpen(false);
    setOpen(true);
  }, []);

  const streamReply = useCallback(async (fullText) => {
    const id = `a-${Date.now()}`;
    setTyping(false);
    setMessages((prev) => [...prev, { id, role: 'agent', text: '' }]);

    const chunkSize = 3;
    for (let i = 0; i < fullText.length; i += chunkSize) {
      if (streamCancelRef.current) return;
      const slice = fullText.slice(0, i + chunkSize);
      setMessages((prev) => prev.map((msg) => (msg.id === id ? { ...msg, text: slice } : msg)));
      const last = fullText[i] || '';
      const delay = /[.\n]/.test(last) ? 28 : /[, ]/.test(last) ? 12 : 8;
      // eslint-disable-next-line no-await-in-loop
      await sleep(delay);
    }

    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, text: fullText } : msg))
    );
  }, []);

  const send = useCallback(
    async (raw) => {
      const text = String(raw || '').trim();
      if (!text || busy) return;

      streamCancelRef.current = false;
      const history = messages
        .filter((msg) => msg.id !== 'welcome' && msg.text)
        .slice(-6)
        .map((msg) => ({
          role: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.text,
        }));

      setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', text }]);
      setInput('');
      setSuggestions([]);
      setBusy(true);
      setTyping(true);

      const result = await api.askWorkspaceAgent({
        question: text,
        locationId: activeLocation?.id,
        history,
      });

      if (streamCancelRef.current) {
        setBusy(false);
        setTyping(false);
        return;
      }

      const reply =
        result.error ||
        result.data?.reply ||
        'I could not find an answer in the current data.';

      await streamReply(reply);
      setSuggestions(DEMO_PROMPTS.filter((prompt) => prompt !== text).slice(0, 2));
      setBusy(false);
    },
    [activeLocation?.id, busy, messages, streamReply]
  );

  return (
    <>
      {/* Bottom-left floating control */}
      {!open && (
        <div className="fixed bottom-5 left-5 z-40 sm:bottom-6 sm:left-6">
          {tipOpen && (
            <div
              role="status"
              className="absolute bottom-full left-0 mb-3 w-60 bg-ink-900 px-3 py-2.5 text-left shadow-[0_20px_40px_-20px_rgba(6,18,15,0.55)] ring-1 ring-sand-100/15"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-sand-50">Ask {SERA.name}</p>
                <button
                  type="button"
                  onClick={() => setTipOpen(false)}
                  aria-label="Dismiss"
                  className="flex h-6 w-6 flex-none items-center justify-center text-sand-200/70 transition-colors hover:bg-sand-50/10 hover:text-sand-50"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="mt-1 pr-1 text-xs leading-snug text-sand-200/80">
                I can help you answer questions about your business.
              </p>
            </div>
          )}
          <button
            type="button"
            onClick={openPanel}
            aria-label={`Ask ${SERA.name}`}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-ink-900 text-saffron-400 shadow-[0_20px_40px_-20px_rgba(6,18,15,0.65)] ring-1 ring-sand-100/20 transition-transform hover:scale-105 hover:bg-ink-800"
          >
            <Sparkles className="h-6 w-6" />
          </button>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <button
            type="button"
            aria-label="Close business agent"
            className="absolute inset-0 bg-ink-950/40 transition-opacity"
            onClick={close}
          />

          <aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="business-agent-title"
            className="relative flex h-full w-full max-w-full flex-col bg-sand-50 shadow-[-20px_0_60px_-30px_rgba(6,18,15,0.45)] ring-1 ring-ink-900/10 sm:w-1/2 sm:max-w-xl lg:max-w-2xl"
          >
            <div className="flex flex-none items-start justify-between gap-3 border-b border-ink-900/10 px-5 py-4">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-saffron-600">
                  <Sparkles className="h-3.5 w-3.5" />
                  Business agent
                </p>
                <h2
                  id="business-agent-title"
                  className="mt-1 font-display text-2xl font-light text-ink-900"
                >
                  {SERA.name}
                </h2>
                <p className="mt-1 text-sm text-ink-700/75">
                  Ask instead of digging through reports
                  {business?.name ? ` · ${business.name}` : ''}.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="flex h-9 w-9 flex-none items-center justify-center text-ink-700 transition-colors hover:bg-ink-900/5"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-5">
                {messages.map((message, index) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[92%] whitespace-pre-wrap px-4 py-3 text-sm leading-relaxed ${
                        message.role === 'user'
                          ? 'bg-ink-900 text-sand-50'
                          : 'bg-white text-ink-900 ring-1 ring-ink-900/10'
                      }`}
                    >
                      {message.text}
                      {message.role === 'agent' &&
                      busy &&
                      !typing &&
                      index === messages.length - 1 &&
                      message.text ? (
                        <span className="ml-0.5 inline-block h-3 w-[2px] animate-pulse bg-saffron-500 align-middle" />
                      ) : null}
                    </div>
                  </div>
                ))}

                {typing && <TypingIndicator />}

                {!busy && suggestions.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-600/55">
                      Suggested
                    </p>
                    <div className="flex flex-col gap-2">
                      {suggestions.map((prompt) => (
                        <button
                          key={prompt}
                          type="button"
                          onClick={() => send(prompt)}
                          className="bg-white px-3 py-2.5 text-left text-sm text-ink-800 ring-1 ring-ink-900/10 transition-colors hover:bg-ink-900 hover:text-sand-50"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>

              <form
                className="flex flex-none gap-2 border-t border-ink-900/10 p-3 sm:p-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  send(input);
                }}
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={busy}
                  placeholder={`Ask ${SERA.name}…`}
                  className="min-w-0 flex-1 bg-white px-4 py-3 text-sm ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500 disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  aria-label="Send"
                  className="inline-flex items-center justify-center bg-saffron-500 px-4 text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default BusinessAgentDock;
